import { Record } from 'immutable';
import { theme } from '@capitalise/ui-design-system';
import Hydrator from 'utils/Hydrator';
import { formatCurrency, formatToOnlyThousands } from 'utils/NumberHelper';
import { BalanceSheetAccountType } from 'enums/balance-sheet-account-types';
import {
  InsightId,
  InsightQuestionnaireTerm,
  InsightRank,
  InsightAction,
  InsightNarrativeVarType,
  InsightTag,
} from 'enums/insight-type';
import {
  CreditScoreTypeLetter,
  getCreditScoreGradeColorClassName,
  getCreditScoreGradeColor,
} from 'enums/debtor-credit-score-type';
import InsightCost from 'models/InsightCostModel';
import InsightProduct from 'models/StubbedInsightProductModel';
import { getAnswerByType } from 'enums/questionnaire-mapping';
import { ProductType } from 'enums/product-type';
import { TermTypesLabels } from 'enums/term-types';
import { FundingPurposeTypeLabel } from 'enums/funding-purpose-type';

const InsightRecord = Record({
  id: '',
  rank: InsightRank.low,
  type: '',
  name: '',
  category: '',
  description: '',
  narrative: '',
  narrativeVars: [],
  cost: new InsightCost(),
  recommendedProducts: [],
  cashPositionChange: 0,
  createdAt: new Date(0),
  tag: '',
  purpose: null,
  hidden: false,
});

export default class Insight extends InsightRecord {
  static deserialize(jsonObject = {}) {
    if (typeof jsonObject === 'undefined' || jsonObject === null) {
      return undefined;
    }

    const mapProperties = {
      createdAt: Hydrator.timestamp2Date,
      recommendedProducts: Hydrator.fromList(InsightProduct.deserialize),
      cost: InsightCost.deserialize,
    };

    const additionalProperties = {
      id: jsonObject.name.replace(/ /gi, '-')?.toLowerCase(),
    };

    const hydrated = Object.keys(jsonObject).reduce(
      Hydrator.assign(jsonObject, mapProperties, {}),
      additionalProperties,
    );

    return new Insight(hydrated);
  }

  get hidden() {
    return this.get('hidden') || this.id === InsightId.debtorRisk;
  }

  get questionnaireTerm() {
    return InsightQuestionnaireTerm[this.id] || 0;
  }

  get action() {
    if (this.id === InsightId.cashRich || this.id === InsightId.debtorRisk) {
      return InsightAction.arrangeCall;
    }

    if (this.id === InsightId.badDebt) {
      return InsightAction.recoveryService;
    }

    return '';
  }

  get roundedCashPosition() {
    if (!this.cashPositionChange || this.cashPositionChange < 0) {
      return 0;
    }
    const nearestCeilingAmount = 5000;
    return (
      Math.round(this.cashPositionChange / nearestCeilingAmount) * nearestCeilingAmount ||
      nearestCeilingAmount
    );
  }

  get narrativeWithHighlightsOldUI() {
    let { narrative } = this;

    this.narrativeVars.forEach(({ value, type }) => {
      if (value === '0') return;

      let formattedValue = value;
      let highligtedVarClassname = 'u-color-warn';

      if (type === InsightNarrativeVarType.currency) {
        formattedValue = formatCurrency(Math.round(value));
      }

      if (type === InsightNarrativeVarType.grade) {
        formattedValue = CreditScoreTypeLetter[value];
        highligtedVarClassname = getCreditScoreGradeColorClassName(value);
      }

      const highlightedVariable = `<strong class=${highligtedVarClassname}>${formattedValue}</strong>`;

      narrative = narrative.replace(new RegExp(`\\b${value}\\b`, 'gi'), highlightedVariable);
    });

    // Prevent XSS
    return narrative.replace(/<script[^>]*>.*?<\/script>/gi, '');
  }

  get narrativeWithHighlights() {
    let { narrative } = this;

    this.narrativeVars.forEach(({ value, type }) => {
      if (value === '0') return;

      let formattedValue = value;
      let highlightColor = 'warning';

      if (type === InsightNarrativeVarType.currency) {
        formattedValue = formatToOnlyThousands(value);
      }

      if (type === InsightNarrativeVarType.grade) {
        formattedValue = CreditScoreTypeLetter[value];
        highlightColor = getCreditScoreGradeColor(value);
      }

      const color = theme.colors[`${highlightColor}-400`];
      const bgColor = theme.colors[`${highlightColor}-50`];
      const highlightedVariable = `<strong style="color: ${color}; background-color: ${bgColor}">${formattedValue}</strong>`;

      const valueWithoutMinus = value && value[0] === '-' ? value.substring(1) : value;

      narrative = narrative.replace(
        new RegExp(`-?\\b${valueWithoutMinus}\\b`, 'gi'),
        highlightedVariable,
      );
    });

    // Prevent XSS
    return narrative.replace(/<script[^>]*>.*?<\/script>/gi, '');
  }

  generateProductMatchesPayload(business, companyDetailsData) {
    const { id, cashPositionChange, recommendedProducts, tag, purpose } = this;

    const payload = [];
    payload.push(getAnswerByType('term', TermTypesLabels[InsightQuestionnaireTerm[id]]));
    payload.push(getAnswerByType('amount', `${cashPositionChange}`));

    if (recommendedProducts.size) {
      recommendedProducts.forEach((product) => {
        payload.push(getAnswerByType('product', product.id));
      });
    }

    if (tag === InsightTag.recoveryLoanScheme) {
      payload.push(getAnswerByType('product', ProductType.governmentBacked));
      payload.push(getAnswerByType('product', ProductType.assetBackedFinance));
      payload.push(getAnswerByType('product', ProductType.invoiceFinancing));
    }

    if (business.companyNumber) {
      payload.push(getAnswerByType('companyNumber', business.companyNumber));
    }

    if (purpose) {
      payload.push(
        getAnswerByType('purpose', { id: purpose, value: FundingPurposeTypeLabel[purpose] }),
      );
    }

    payload.push(
      (companyDetailsData?.sector && getAnswerByType('sector', companyDetailsData.sector)) ||
        (companyDetailsData?.normalizedSectorId &&
          getAnswerByType('sector', companyDetailsData.normalizedSectorId)),
      companyDetailsData?.companyType &&
        getAnswerByType('companyType', companyDetailsData.companyType),
      companyDetailsData?.companyName &&
        getAnswerByType('companyName', companyDetailsData.companyName),
    );

    return payload.filter(Boolean);
  }

  get accountType() {
    // TODO: this getter and all related logic should be removed/updated when snapshot.ledgerInsights[i] will include accountType field
    const map = {
      [InsightId.badDebt]: null,
      [InsightId.debtorRisk]: BalanceSheetAccountType.debtors,
      [InsightId.unmatchedLiabilities]: null,
      [InsightId.largeDebtorBook]: BalanceSheetAccountType.debtors,
      [InsightId.overdraftUtilised]: BalanceSheetAccountType.bank,
      [InsightId.highFixedAssets]: BalanceSheetAccountType.fixedAssets,
      [InsightId.insufficientCash]: BalanceSheetAccountType.shortTermLiability,
      [InsightId.cashRich]: BalanceSheetAccountType.bank,
      [InsightId.poorLiquidity]: BalanceSheetAccountType.shortTermLiability,
      [InsightId.highEquity]: BalanceSheetAccountType.shortTermLiability,
    };

    return map[this.id];
  }
}
